import { useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePermission } from "@/hooks/use-permission";
import {
  type CampaignCreationTaskScope,
  campaignCreationTaskScopeKey,
  campaignCreationTasksKey,
  fetchCampaignCreationTasks,
  subscribeToCampaignCreationTasks,
} from "@/lib/campaign-creation-tasks";
import { SSE_FALLBACK_POLL_MS, SSE_SAFETY_POLL_MS } from "@/lib/sse-invalidation";
import { useAuthStore } from "@/stores/auth";

export function useCampaignCreationTaskScope() {
  const user = useAuthStore((state) => state.user);
  const shops = useAuthStore((state) => state.shops);
  const { hasModule } = usePermission();
  const workspaceId = user?.activeWorkspaceId;
  const workspaceShops = shops.filter((shop) => shop.workspaceId === workspaceId);
  const accessFingerprint = JSON.stringify({
    role: user?.role,
    isMainAccount: user?.isMainAccount,
    modules: user?.modulePermissions,
    shops: workspaceShops
      .map((shop) => ({
        id: shop.shopId,
        role: shop.role,
        permissions: [...shop.permissions].sort(),
      }))
      .sort((left, right) => left.id.localeCompare(right.id)),
  });
  const userId = user?.id;
  const canViewTasks = hasModule("campaigns") && workspaceShops.length > 0;
  return useMemo<CampaignCreationTaskScope | null>(
    () =>
      userId && workspaceId && canViewTasks ? { userId, workspaceId, accessFingerprint } : null,
    [userId, workspaceId, canViewTasks, accessFingerprint],
  );
}

export function useCampaignCreationTasks() {
  const queryClient = useQueryClient();
  const scope = useCampaignCreationTaskScope();
  const shops = useAuthStore((state) => state.shops);
  const identity = scope ? JSON.stringify(scope) : null;
  const [subscribedIdentity, setSubscribedIdentity] = useState<string | null>(null);
  const [forbiddenIdentity, setForbiddenIdentity] = useState<string | null>(null);
  const [sseFailed, setSseFailed] = useState(false);
  const observedCompletions = useRef(new Set<string>());
  const completionsSeeded = useRef(false);
  const authorized = Boolean(scope && identity !== forbiddenIdentity);
  useEffect(() => {
    setForbiddenIdentity((previous) => (previous === identity ? previous : null));
    observedCompletions.current.clear();
    completionsSeeded.current = false;
  }, [identity]);
  const query = useQuery({
    queryKey: campaignCreationTasksKey(scope),
    queryFn: ({ signal }) => fetchCampaignCreationTasks(signal),
    enabled: authorized && identity === subscribedIdentity,
    staleTime: 15_000,
    refetchInterval: sseFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) =>
      !(error instanceof HTTPError && [401, 403].includes(error.response.status)) &&
      failureCount < 2,
  });

  useEffect(() => {
    if (!scope || !authorized) return;
    let current = true;
    setSseFailed(false);
    // Subscribe before enabling the GET; hints during initialization are coalesced
    // into another authoritative read by the shared SSE invalidation helper.
    const unsubscribe = subscribeToCampaignCreationTasks(queryClient, scope, (state) => {
      if (current) {
        setSseFailed(state === "error");
        if (state === "open")
          void queryClient.invalidateQueries({ queryKey: campaignCreationTaskScopeKey(scope) });
      }
    });
    setSubscribedIdentity(identity);
    return () => {
      current = false;
      unsubscribe();
      void queryClient.cancelQueries({ queryKey: campaignCreationTaskScopeKey(scope) });
      queryClient.removeQueries({ queryKey: campaignCreationTaskScopeKey(scope) });
    };
  }, [queryClient, scope, authorized, identity]);

  useEffect(() => {
    if (query.error instanceof HTTPError && [401, 403].includes(query.error.response.status)) {
      setForbiddenIdentity(identity);
    }
  }, [query.error, identity]);

  useEffect(() => {
    if (!scope || !authorized) return;
    const refresh = () => {
      if (document.visibilityState === "visible") {
        void queryClient.invalidateQueries({
          queryKey: campaignCreationTasksKey(scope),
          exact: true,
        });
      }
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("online", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [queryClient, scope, authorized]);

  useEffect(() => {
    if (!authorized || !query.data) return;
    const completed = query.data.data.filter(
      (task) =>
        task.status === "completed" &&
        task.executionMode === "amazon" &&
        task.identityVerification === "verified" &&
        !observedCompletions.current.has(task.id),
    );
    for (const task of completed) observedCompletions.current.add(task.id);
    // The first snapshot only records what already finished: lists mounted on this page load
    // read fresh data anyway, so only completions observed afterwards need invalidation.
    if (!completionsSeeded.current) {
      completionsSeeded.current = true;
      return;
    }
    if (!completed.length) return;
    // Amazon readback has completed; existing lists can now safely surface the new resources.
    for (const key of [["campaigns"], ["ai-managed-groups"], ["dashboard"]]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  }, [authorized, query.data, queryClient]);

  const denied =
    query.error instanceof HTTPError && [401, 403].includes(query.error.response.status);
  const allowedShopIds = new Set(
    shops.filter((shop) => shop.workspaceId === scope?.workspaceId).map((shop) => shop.shopId),
  );
  return {
    scope,
    identity,
    tasks:
      authorized && !denied && identity === subscribedIdentity
        ? (query.data?.data.filter((task) => allowedShopIds.has(task.shopId)) ?? [])
        : [],
    hasMore: query.data?.pagination.hasMore ?? false,
    reconnecting: sseFailed || query.isError,
  };
}
